import React, { useMemo, useState } from 'react';
import { Drawer, Alert, Spin, Button, Input, message } from 'antd';
import { Edit, Save, Close } from '@styled-icons/material';
import styled from 'styled-components';
import { useMarkdown } from '@shared/hooks/useMarkdown';
import theme from '@config/theme';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize from 'rehype-sanitize';
import ScopedComponent from '@ui/Utility/ScopedComponent';
import useFirebase from '@app/providers/firebase';
import { ref as storageRef, uploadString } from 'firebase/storage';
import { useRecoilValue } from 'recoil';

interface MarkdownHelpDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  markdownPath?: string | null;
  title?: string;
  width?: number;
}

const Container = styled.div`
  padding: 16px 24px 48px 24px;
  color: ${theme.palette.neutral.black};

  h1, h2, h3, h4, h5 {
    color: ${theme.palette.primary.hue};
    margin-top: 1.2em;
  }
  img { max-width: 100%; height: auto; }
  pre { background: #0f172a; color: #e2e8f0; padding: 12px; border-radius: 6px; overflow: auto; }
  code { background: #0f172a; color: #e2e8f0; padding: 2px 6px; border-radius: 4px; }
  table { width: 100%; border-collapse: collapse; }
  th, td { border: 1px solid #e5e7eb; padding: 6px 8px; }
`;

const EditButton = styled(Button)`
  position: absolute;
  top: 16px;
  right: 56px;
  z-index: 10;
`;

const EditIcon = styled(Edit)`
  width: 18px;
  margin-right: 6px;
`;

const SaveIcon = styled(Save)`
  width: 18px;
  margin-right: 6px;
`;

const CancelIcon = styled(Close)`
  width: 18px;
  margin-right: 6px;
`;

const EditorContainer = styled.div`
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  height: calc(100vh - 120px);
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 12px;
`;

const Loader = () => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
    <Spin />
  </div>
);

export const MarkdownHelpDrawer: React.FC<MarkdownHelpDrawerProps> = ({
  isOpen,
  onClose,
  markdownPath,
  title = 'Documentation',
  width = 720,
}) => {
  const { content, isLoading, error, refetch, resolvedUrl } = useMarkdown(markdownPath);
  const { storage } = useFirebase();
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedContent, setEditedContent] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const drawerTitle = useMemo(() => title, [title]);

  const handleEditClick = () => {
    setEditedContent(content || '');
    setIsEditMode(true);
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditedContent('');
  };

  const handleSave = async () => {
    if (!markdownPath) {
      message.error('No markdown path specified');
      return;
    }

    setIsSaving(true);
    try {
      const pathInBucket = markdownPath.startsWith('/') ? markdownPath.replace(/^\//, '') : markdownPath;
      const fileRef = storageRef(storage, pathInBucket);
      await uploadString(fileRef, editedContent, 'raw', {
        contentType: 'text/markdown',
        cacheControl: 'public, max-age=300',
      });
      message.success('Documentation updated successfully');
      setIsEditMode(false);
      refetch();
    } catch (err) {
      console.error('Error saving markdown:', err);
      message.error('Failed to save documentation');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Drawer
      title={drawerTitle}
      placement="right"
      width={width}
      open={isOpen}
      onClose={onClose}
      mask={false}
      destroyOnClose
      extra={
        <ScopedComponent requiredPermissions={['*']}>
          {!isEditMode ? (
            <EditButton type="default" onClick={handleEditClick} disabled={isLoading || !!error}>
              <EditIcon />
              Edit
            </EditButton>
          ) : null}
        </ScopedComponent>
      }
    >
      {error && (
        <Alert
          style={{ marginBottom: 12 }}
          type="error"
          showIcon
          message="Unable to load documentation"
          description={
            <div>
              <div style={{ marginBottom: 8 }}>{error.message}</div>
              {(resolvedUrl || markdownPath) && (
                <div>
                  <a href={resolvedUrl || markdownPath || undefined} target="_blank" rel="noreferrer">Open raw markdown</a>
                  <span style={{ margin: '0 8px' }}>/</span>
                  <a onClick={refetch}>Retry</a>
                </div>
              )}
            </div>
          }
        />
      )}

      {isLoading && <Loader />}

      {isEditMode ? (
        <EditorContainer>
          <Input.TextArea
            value={editedContent}
            onChange={(e) => setEditedContent(e.target.value)}
            style={{ flex: 1, fontFamily: 'monospace', fontSize: 14 }}
            placeholder="Enter markdown content..."
          />
          <ButtonRow>
            <Button type="primary" onClick={handleSave} loading={isSaving} icon={<SaveIcon />}>
              Save
            </Button>
            <Button onClick={handleCancelEdit} disabled={isSaving} icon={<CancelIcon />}>
              Cancel
            </Button>
          </ButtonRow>
        </EditorContainer>
      ) : (
        <>
          {!isLoading && content && (
            <Container>
              <ReactMarkdown remarkPlugins={[remarkGfm as any]} rehypePlugins={[rehypeSanitize as any]}>
                {content}
              </ReactMarkdown>
            </Container>
          )}
          {!isLoading && !content && !error && (
            <Alert type="info" showIcon message="No content" description="This document is empty." />
          )}
        </>
      )}
    </Drawer>
  );
};

export default MarkdownHelpDrawer;


